import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { agentNodeToAccount, levelToRole, type AgentNode } from '@/api/map/agent'
import { loginEmailFromUsername, toCreateAgentRequest } from '@/api/map/create'
import * as api from '@/data/api'
import type { Account } from '@/data/types'
import { ROLE_LABEL, creatableRoles } from '@/data/types'
import { keys } from '@/api/hooks/shared/keys'

export type CreatedAccount = Account & {
  /** Keycloak login email when provisioned (e.g. testdemo@rimora.org). */
  loginEmail?: string
  loginReady?: boolean
}

async function createAccountLive(input: api.CreateAccountInput): Promise<CreatedAccount> {
  const parentId = getCallerAgentId()!
  const parentNode = await apiFetch<AgentNode>(`/v1/agents/${parentId}`)
  const parentRole = levelToRole(parentNode.level)
  const allowed = creatableRoles(parentRole)
  if (!allowed.includes(input.role)) {
    throw new Error(
      `As a ${ROLE_LABEL[parentRole]} you can only create ${allowed.map((r) => ROLE_LABEL[r]).join(' or ')} — not ${ROLE_LABEL[input.role]}`,
    )
  }

  const body = toCreateAgentRequest(input, parentId)
  const node = await apiFetch<AgentNode & { loginReady?: boolean; loginEmail?: string; username?: string }>(
    '/v1/agents',
    {
      method: 'POST',
      headers: {
        'Idempotency-Key': crypto.randomUUID(),
      },
      body: JSON.stringify(body),
    },
  )

  // Mock returns opening balance = creditLimit (Backend-UI). Live create
  // response has no balance field → agentNodeToAccount maps that to 0 until Give.
  const account = agentNodeToAccount(node)
  const loginReady = Boolean(node.loginReady)
  const loginEmail =
    node.loginEmail?.trim() ||
    (input.password ? loginEmailFromUsername(input.username) : undefined)

  return {
    ...account,
    username: input.username.trim() || node.username || account.username,
    displayName: input.displayName.trim() || account.displayName,
    commission: input.commission,
    betLimits: input.betLimits,
    games: input.games,
    comments: input.comments,
    flags: loginReady ? [] : ['no-login-yet'],
    loginEmail,
    loginReady,
  }
}

async function createAccount(input: api.CreateAccountInput): Promise<CreatedAccount> {
  if (hasLiveApi()) return createAccountLive(input)
  const account = await api.createAccount(input)
  // Fixture path has no Keycloak — keep the honest no-login flag.
  return { ...account, loginReady: false }
}

export function useCreateAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['downline'] })
      client.invalidateQueries({ queryKey: ['children'] })
      client.invalidateQueries({ queryKey: ['overview'] })
      client.invalidateQueries({ queryKey: keys.session })
      client.invalidateQueries({ queryKey: keys.createParent })
    },
  })
}
